// src/pages/sqf/SqfTemplateListPage.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import sqfService from '../../services/sqfService';
import { useLanguage, t } from '../../context/LanguageContext';
import SqfNav from '../../components/SqfNav';
import ConfirmModal from '../../components/ConfirmModal';

const CATEGORY_LABELS = {
  SOP:   'SOP',
  SSOP:  'SSOP',
  SPC:   'SPC',
  SCPC:  'SCPC',
  CCP:   'CCP',
  OTHER: 'Other',
};

const CATEGORY_COLORS = {
  SOP:   'primary',
  SSOP:  'info',
  SPC:   'warning',
  SCPC:  'secondary',
  CCP:   'danger',
  OTHER: 'light',
};

const TRIGGER_LABELS = {
  scheduled:          'Scheduled',
  before_production:  'Before Production',
  after_production:   'After Production',
  on_demand:          'On Demand',
};

const TRIGGER_COLORS = {
  scheduled:          'secondary',
  before_production:  'danger',
  after_production:   'warning',
  on_demand:          'success',
};

const TYPE_LABELS = {
  pre_op:          'Pre-Op',
  metal_detector:  'Metal Detector',
  sanitizer:       'Sanitizer',
  thermometer:     'Thermometer',
  cleaning:        'Cleaning',
  end_of_shift:    'End of Shift',
  cold_storage:    'Cold Storage',
  visitor_log:     'Visitor Log',
  contractor_log:  'Contractor Log',
  custom:          'Custom',
};

export default function SqfTemplateListPage() {
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // New template modal
  const [showModal, setShowModal] = useState(false);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState('custom');
  const [newCategory, setNewCategory] = useState('OTHER');
  const [newTrigger, setNewTrigger] = useState('scheduled');
  const [newDesc, setNewDesc] = useState('');
  const [creating, setCreating] = useState(false);
  const [filterCategory, setFilterCategory] = useState('');
  const [confirmModal, setConfirmModal] = useState(null);

  useEffect(() => {
    sqfService.getTemplates()
      .then(setTemplates)
      .catch(() => setError(t('Failed to load templates.', 'Error al cargar las plantillas.', lang)))
      .finally(() => setLoading(false));
  }, [lang]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const tmpl = await sqfService.createTemplate({ name: newName, checklistType: newType, category: newCategory, triggerType: newTrigger, description: newDesc || null });
      setShowModal(false);
      setNewName(''); setNewType('custom'); setNewCategory('OTHER'); setNewTrigger('scheduled'); setNewDesc('');
      navigate(`/sqf/templates/${tmpl.id}/edit`);
    } catch {
      setError(t('Failed to create template.', 'Error al crear la plantilla.', lang));
    } finally {
      setCreating(false);
    }
  };

  const handleArchive = (tmpl) => {
    const neverPublished = tmpl.activeVersionId === null;
    setConfirmModal({
      title: neverPublished ? t('Delete Template', 'Eliminar Plantilla', lang) : t('Archive Template', 'Archivar Plantilla', lang),
      message: neverPublished
        ? t(
            `"${tmpl.name}" has never been published — this will permanently delete it and its draft content. This cannot be undone.`,
            `"${tmpl.name}" nunca se ha publicado — esto la eliminará permanentemente junto con su contenido borrador. Esta acción no se puede deshacer.`,
            lang
          )
        : t(
            `Archive template "${tmpl.name}"? It will no longer generate new instances.`,
            `¿Archivar la plantilla "${tmpl.name}"? Ya no generará nuevas instancias.`,
            lang
          ),
      confirmLabel: neverPublished ? t('Delete', 'Eliminar', lang) : t('Archive', 'Archivar', lang),
      variant: 'danger',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await sqfService.archiveTemplate(tmpl.id);
          setTemplates(ts => ts.filter(t => t.id !== tmpl.id));
        } catch {
          setError(neverPublished
            ? t('Failed to delete template.', 'Error al eliminar la plantilla.', lang)
            : t('Failed to archive template.', 'Error al archivar la plantilla.', lang));
        }
      },
      onCancel: () => setConfirmModal(null),
    });
  };

  const active = templates.filter(t => !t.archivedAt && (!filterCategory || t.category === filterCategory));
  const archived = templates.filter(t => t.archivedAt);

  return (
    <>
      <SqfNav />
      <div className="container mt-4 pb-5">
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div>
          <Link to="/sqf" className="text-muted" style={{ fontSize: 12 }}>← {t('Dashboard', 'Panel', lang)}</Link>
          <h4 className="mb-0 mt-1">{t('Checklist Templates', 'Plantillas de Lista de Verificación', lang)}</h4>
        </div>
        <div className="d-flex gap-2">
          <Link to="/sqf/policies" className="btn btn-outline-secondary btn-sm">{t('Policies', 'Políticas', lang)}</Link>
          <Link to="/sqf/contractors" className="btn btn-outline-secondary btn-sm">{t('Contractors', 'Contratistas', lang)}</Link>
          <Link to="/sqf/ai-import" className="btn btn-outline-primary btn-sm">✨ {t('Import with AI', 'Importar con IA', lang)}</Link>
          <button className="btn btn-primary btn-sm" onClick={() => setShowModal(true)}>+ {t('New Template', 'Nueva Plantilla', lang)}</button>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2">{error}<button className="btn-close float-end btn-sm" onClick={() => setError('')} /></div>}

      <div className="d-flex gap-2 mb-3 flex-wrap">
        {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
          <button key={k}
            className={`btn btn-sm ${filterCategory === k ? `btn-${CATEGORY_COLORS[k] || 'secondary'}` : 'btn-outline-secondary'}`}
            onClick={() => setFilterCategory(filterCategory === k ? '' : k)}>
            {v}
          </button>
        ))}
        {filterCategory && <button className="btn btn-sm btn-link text-muted p-0 ms-1" onClick={() => setFilterCategory('')}>{t('Clear', 'Limpiar', lang)}</button>}
      </div>

      {loading ? (
        <div className="text-center py-5 text-muted"><div className="spinner-border spinner-border-sm me-2" />{t('Loading…', 'Cargando…', lang)}</div>
      ) : active.length === 0 ? (
        <div className="text-center py-5 text-muted">
          <div style={{ fontSize: 40 }}>📋</div>
          <p className="mt-2">{t('No templates yet.', 'Aún no hay plantillas.', lang)}</p>
          <button className="btn btn-primary btn-sm" onClick={() => setShowModal(true)}>+ {t('New Template', 'Nueva Plantilla', lang)}</button>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm table-bordered table-hover">
            <thead className="table-light">
              <tr>
                <th>{t('Name', 'Nombre', lang)}</th>
                <th>{t('Category', 'Categoría', lang)}</th>
                <th>{t('Type', 'Tipo', lang)}</th>
                <th style={{ width: 80 }}>{t('SQF Ref', 'Ref SQF', lang)}</th>
                <th>{t('Version', 'Versión', lang)}</th>
                <th>{t('Status', 'Estado', lang)}</th>
                <th style={{ width: 180 }}></th>
              </tr>
            </thead>
            <tbody>
              {active.map(tmpl => {
                const publishedVersion = (tmpl.Versions || []).find(v => v.status === 'published');
                const draftVersion = (tmpl.Versions || []).find(v => v.status === 'draft');
                return (
                  <tr key={tmpl.id}>
                    <td className="fw-semibold">
                      <div className="d-flex align-items-center gap-2">
                        <span>{tmpl.name}</span>
                        <span style={{ letterSpacing: 1, fontSize: 14, lineHeight: 1 }} title="English">🇺🇸</span>
                        {tmpl.nameEs && (
                          <span style={{ fontSize: 14, lineHeight: 1 }} title="Español disponible">🇪🇸</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="d-flex flex-column gap-1">
                        {tmpl.category && (
                          <span className={`badge bg-${CATEGORY_COLORS[tmpl.category] || 'light'} ${tmpl.category === 'OTHER' ? 'text-dark border' : ''}`}>
                            {CATEGORY_LABELS[tmpl.category] || tmpl.category}
                          </span>
                        )}
                        {tmpl.triggerType && (
                          <span className={`badge bg-${TRIGGER_COLORS[tmpl.triggerType] || 'secondary'} bg-opacity-25 text-${TRIGGER_COLORS[tmpl.triggerType] || 'secondary'} border border-opacity-50`} style={{ fontSize: 10 }}>
                            {TRIGGER_LABELS[tmpl.triggerType] || tmpl.triggerType}
                          </span>
                        )}
                      </div>
                    </td>
                    <td><span className="badge bg-light text-dark border">{TYPE_LABELS[tmpl.checklistType] || tmpl.checklistType}</span></td>
                    <td style={{ fontSize: 11, color: '#666' }}>{tmpl.sqfReference || <span className="text-muted">—</span>}</td>
                    <td style={{ fontSize: 12 }}>
                      {publishedVersion ? `v${publishedVersion.versionNumber}` : '—'}
                      {draftVersion && <span className="badge bg-secondary ms-1" style={{ fontSize: 9 }}>{t('draft', 'borrador', lang)}</span>}
                    </td>
                    <td>
                      {tmpl.activeVersionId
                        ? <span className="badge bg-success">{t('Published', 'Publicado', lang)}</span>
                        : <span className="badge bg-secondary">{t('No published version', 'Sin versión publicada', lang)}</span>
                      }
                    </td>
                    <td className="text-end" style={{ whiteSpace: 'nowrap' }}>
                      <Link to={`/sqf/templates/${tmpl.id}/edit`} className="btn btn-outline-primary btn-sm me-1">{t('Edit', 'Editar', lang)}</Link>
                      {tmpl.activeVersionId && tmpl.triggerType !== 'before_production' && tmpl.triggerType !== 'after_production' && tmpl.triggerType !== 'on_demand' && (
                        <Link to={`/sqf/templates/${tmpl.id}/schedule`} className="btn btn-outline-secondary btn-sm me-1">{t('Schedule', 'Programar', lang)}</Link>
                      )}
                      {tmpl.activeVersionId && (
                        <Link to={`/sqf/templates/${tmpl.id}/audit`} className="btn btn-outline-secondary btn-sm me-1">{t('Audit Log', 'Historial de Auditoría', lang)}</Link>
                      )}
                      <button className="btn btn-outline-danger btn-sm" onClick={() => handleArchive(tmpl)}>
                        {tmpl.activeVersionId === null ? t('Delete', 'Eliminar', lang) : t('Archive', 'Archivar', lang)}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {archived.length > 0 && (
        <details className="mt-3">
          <summary className="text-muted" style={{ fontSize: 13, cursor: 'pointer' }}>{t('Archived', 'Archivados', lang)} ({archived.length})</summary>
          <ul className="mt-2 list-unstyled ps-2">
            {archived.map(t => (
              <li key={t.id} className="text-muted d-flex align-items-center gap-2" style={{ fontSize: 13 }}>
                <span>{t.name}</span>
                <span style={{ fontSize: 12 }}>🇺🇸</span>
                {t.nameEs && <span style={{ fontSize: 12 }}>🇪🇸</span>}
              </li>
            ))}
          </ul>
        </details>
      )}

      {/* New template modal */}
      {showModal && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t('New Checklist Template', 'Nueva Plantilla de Lista de Verificación', lang)}</h5>
                <button className="btn-close" onClick={() => setShowModal(false)} />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label fw-semibold">{t('Name', 'Nombre', lang)} <span className="text-danger">*</span></label>
                  <input className="form-control" value={newName} onChange={e => setNewName(e.target.value)} placeholder={t('e.g. Pre-Op Sanitation Check', 'ej. Verificación de Saneamiento Pre-Op', lang)} autoFocus />
                </div>
                <div className="row g-3 mb-3">
                  <div className="col">
                    <label className="form-label fw-semibold">{t('Category', 'Categoría', lang)} <span className="text-danger">*</span></label>
                    <select className="form-select" value={newCategory} onChange={e => setNewCategory(e.target.value)}>
                      {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                  <div className="col">
                    <label className="form-label fw-semibold">{t('Type', 'Tipo', lang)}</label>
                    <select className="form-select" value={newType} onChange={e => setNewType(e.target.value)}>
                      {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold">{t('Trigger', 'Disparador', lang)}</label>
                  <select className="form-select" value={newTrigger} onChange={e => setNewTrigger(e.target.value)}>
                    {Object.entries(TRIGGER_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                  {newTrigger === 'before_production' && (
                    <div className="form-text text-danger">{t('This checklist must be completed before production can start each day. No schedule needed.', 'Esta lista de verificación debe completarse antes de que comience la producción cada día. No se necesita horario.', lang)}</div>
                  )}
                  {newTrigger === 'after_production' && (
                    <div className="form-text text-warning">{t('This checklist is triggered when a production run is completed. No schedule needed.', 'Esta lista de verificación se activa cuando se completa una corrida de producción. No se necesita horario.', lang)}</div>
                  )}
                  {newTrigger === 'on_demand' && (
                    <div className="form-text text-muted">{t('Created manually on demand. No schedule or production gate.', 'Creado manualmente bajo demanda. Sin horario ni restricción de producción.', lang)}</div>
                  )}
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold">{t('Description', 'Descripción', lang)}</label>
                  <textarea className="form-control" rows={2} value={newDesc} onChange={e => setNewDesc(e.target.value)} />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowModal(false)}>{t('Cancel', 'Cancelar', lang)}</button>
                <button className="btn btn-primary" onClick={handleCreate} disabled={creating || !newName.trim()}>
                  {creating ? t('Creating…', 'Creando…', lang) : t('Create & Edit', 'Crear y Editar', lang)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        show={!!confirmModal}
        title={confirmModal?.title}
        message={confirmModal?.message}
        confirmLabel={confirmModal?.confirmLabel}
        variant={confirmModal?.variant}
        onConfirm={confirmModal?.onConfirm}
        onCancel={confirmModal?.onCancel}
      />
    </div>
    </>
  );
}
